import useTranslator from "../../hooks/useTranslator";
import { useContext, useEffect, useState } from "react";
import LandLordContext from "../../context/landlord/LandLordContext";
import {
  getInstitutionalDomainName,
  haveValue,
  isDefaultWorkspace,
  isOwnerOfWorkspace,
  toast,
} from "../../helper/common";
import ConfirmMemberRemovalModal from "./ConfirmMemberRemovalModal";
import AuthContext from "../../context/auth/AuthContext";
import { WORKSPACE_ROLES_CAPABILITIES } from "../../Utils/workspaceRoleCapabilities";
import {
  Avatar,
  Badge,
  Button,
  Icon,
  Select,
  SkeletonList,
  TextField,
} from "../design";

const EMAIL_PATTERN = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

/** Members tab of "Manage workspace": invite a landlord, change roles, remove. */
const ManageMembers = ({ manageMembers }) => {
  const { _t } = useTranslator();
  const allRoles = WORKSPACE_ROLES_CAPABILITIES?.roleHierarchyBasedOnIndex;
  const { getWorkspaceMembers, inviteWorkspaceMembers, updateWorkspaceRoles } =
    useContext(LandLordContext);
  const { user } = useContext(AuthContext);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("BRONZE");
  const [localLoader, setLocalLoader] = useState(false);
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [removalLandlord, setRemovalLandlord] = useState(null);

  const myRoleIndex = allRoles?.indexOf(user?.current_workspace_role);
  const roleLabel = (role) => _t(`${role?.toLowerCase()}Member`);
  // roles up to my own, never OWNER; highest first
  const rolesICanGive = (allRoles ?? [])
    .filter((role) => allRoles.indexOf(role) <= myRoleIndex && role !== "OWNER")
    .reverse();

  useEffect(() => {
    defaultFunc();
  }, [manageMembers]);

  const defaultFunc = () => {
    setLocalLoader(true);
    getWorkspaceMembers()
      .then((res) => {
        setWorkspaceMembers(res?.success_res?.workspaceMembers ?? []);
      })
      .catch((err) => {
        console.log(err);
        toast.error(_t("errorWhileFetchingWorkspaceMembers"));
      })
      .finally(() => {
        setLocalLoader(false);
      });
  };

  const handleSendInvite = (e) => {
    e.preventDefault();
    if (!EMAIL_PATTERN.test(inviteEmail)) {
      toast.error(_t("invalidEmailAddress"));
      return;
    }
    setIsSendingInvite(true);
    inviteWorkspaceMembers({ email: inviteEmail, role: inviteRole })
      .then(() => {
        setInviteEmail("");
        defaultFunc();
      })
      .catch((err) => {
        if (haveValue(err?.response?.data?.message?.key)) {
          toast.error(_t(err?.response?.data?.message?.key));
        } else {
          toast.error(_t("errorWhileSendingInvite"));
        }
      })
      .finally(() => {
        setIsSendingInvite(false);
      });
  };

  const changeMemberRole = (newRole, memberVal, index) => {
    setWorkspaceMembers((prev) =>
      prev.map((m, i) => (i === index ? { ...m, role: newRole } : m))
    );
    updateWorkspaceRoles({ user_id: memberVal?._id, new_role: newRole }).catch(
      (err) => {
        console.log(err);
        defaultFunc();
        toast.error(_t("errorWhileUpdatingMemberRole"));
      }
    );
  };

  // index is the position in the full list (the first one is the creator of
  // the default workspace)
  const canEditMember = (val, index) =>
    !(index === 0 && isDefaultWorkspace(user)) &&
    !isOwnerOfWorkspace(user, val) &&
    allRoles?.indexOf(val?.role) <= myRoleIndex;

  const memberName = (val) =>
    haveValue(val?.landlordId)
      ? `${val?.landlordId?.fname_companyName ?? ""} ${
          val?.landlordId?.lname ?? ""
        }`.trim()
      : val?.email;

  const members = workspaceMembers
    .map((val, index) => ({ val, index }))
    .filter(({ val }) => val?.status !== "invited");
  const invited = workspaceMembers.filter((val) => val?.status === "invited");

  return (
    <div className="workspace-members">
      <form className="workspace-members__invite" onSubmit={handleSendInvite}>
        <h3 className="workspace-section-title">{_t("inviteLLToWorkspace")}</h3>
        <div className="workspace-members__invite-row">
          <TextField
            id="workspace-invite-email"
            type="email"
            size="sm"
            aria-label={_t("enterMemberEmail")}
            placeholder={_t("enterMemberEmail")}
            icon={<Icon name="mail" />}
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value.trim())}
          />
          <Select
            id="workspace-invite-role"
            size="sm"
            aria-label={_t("role")}
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
            options={rolesICanGive.map((role) => ({
              value: role,
              label: roleLabel(role),
            }))}
          />
          <Button
            type="submit"
            iconStart={<Icon name="send" size={16} />}
            disabled={!haveValue(inviteEmail)}
            loading={isSendingInvite}
          >
            {_t("sendInvite")}
          </Button>
        </div>
        <p className="workspace-members__role-hint">
          {_t(`${inviteRole?.toLowerCase()}Description`)}
        </p>
      </form>

      <h3 className="workspace-section-title">{_t("members")}</h3>
      {localLoader ? (
        <SkeletonList rows={3} avatarSize={28} label={_t("loading")} />
      ) : (
        <ul className="workspace-members__list">
          {members.map(({ val, index }) => {
            const editable = canEditMember(val, index);
            const domain = getInstitutionalDomainName(val?.landlordId?.email);
            const canRemove =
              editable &&
              val?.landlordId?._id?.toString() !==
                user?.current_workspace?.created_by;
            const roleOptions = rolesICanGive.filter((r) => r !== "GUEST");
            return (
              <li key={val?._id ?? index} className="workspace-members__item">
                <Avatar name={memberName(val)} size="sm" />
                <span className="workspace-members__who">
                  <span className="workspace-members__name">
                    {memberName(val)}
                  </span>
                  {haveValue(domain) && <Badge tone="neutral">{domain}</Badge>}
                </span>
                {editable ? (
                  <>
                    <Select
                      id={`workspace-member-role-${index}`}
                      size="sm"
                      aria-label={_t("role")}
                      className="workspace-members__role"
                      value={val?.role}
                      onChange={(e) =>
                        changeMemberRole(e.target.value, val, index)
                      }
                      options={[
                        ...(roleOptions.includes(val?.role)
                          ? []
                          : [
                              {
                                value: val?.role,
                                label: roleLabel(val?.role),
                                disabled: true,
                              },
                            ]),
                        ...roleOptions.map((role) => ({
                          value: role,
                          label: roleLabel(role),
                        })),
                      ]}
                    />
                    {canRemove ? (
                      <Button
                        variant="ghost"
                        size="md"
                        iconOnly
                        aria-label={_t("remove")}
                        title={_t("remove")}
                        iconStart={<Icon name="trash" size={18} />}
                        onClick={() => setRemovalLandlord(val)}
                      />
                    ) : (
                      <span className="workspace-members__spacer" />
                    )}
                  </>
                ) : (
                  <Badge tone="neutral">{roleLabel(val?.role)}</Badge>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {invited.length > 0 && (
        <>
          <h3 className="workspace-section-title">{_t("pendingInvites")}</h3>
          <ul className="workspace-members__list">
            {invited.map((val, i) => (
              <li key={val?._id ?? i} className="workspace-members__item">
                <Avatar name={val?.email} size="sm" />
                <span className="workspace-members__who">
                  <span className="workspace-members__name">{val?.email}</span>
                </span>
                <Badge tone="warning">{_t("inviteSent")}</Badge>
              </li>
            ))}
          </ul>
        </>
      )}

      {removalLandlord && (
        <ConfirmMemberRemovalModal
          onClose={() => setRemovalLandlord(null)}
          llData={removalLandlord}
          refreshFunc={defaultFunc}
        />
      )}
    </div>
  );
};

export default ManageMembers;
